import { ConfigProvider, ThemeConfig } from "antd";
import { SecureComApp } from "common/utils/universal/types";
import React from "react";
import { daThemeClass, vkThemeClass } from "./theme.css";
import themeColors from "./themeColors";

/**
 * An important note about themes: AntDesign requires colors to be hex codes, whereas vanilla-extract requires css variables. Use `themeColors` for AntDesign components and use `themeContract.colors` for css.
 */
const ThemeContext = ({ children }: { children: React.ReactNode }) => {
  // Select the vanilla-extract theme class at runtime
  //Important because vanilla-extract uses a buildtime compiler
  const themeClass =
    process.env.REACT_APP_NAME === SecureComApp.DA
      ? daThemeClass
      : vkThemeClass;

  // AntD theme tokens (currently using concrete values from themeColors)
  // Do not override styles here if possible, prefer using vanilla-extract in component css file instead. For example, see Button.css.
  const themeConfig: ThemeConfig = {
    token: {
      colorError: themeColors.error.main,
      colorInfo: themeColors.info.main,
      colorPrimary: themeColors.primary.main,
      colorPrimaryActive: themeColors.primary.mid,
      colorPrimaryHover: themeColors.primary.dark,
      colorSuccess: themeColors.success.main,
      colorWarning: themeColors.warning.main,
      controlOutline: themeColors.primary.light,
      colorTextDisabled: themeColors.neutral.dark,
      colorBgContainerDisabled: themeColors.neutral.xlight,
      colorSuccessBg: themeColors.success.light,
      // The @sc/ui Modal is pinned to zIndex 10000 (to sit above app banners),
      // so popup components (Select, DatePicker, Tooltip, etc.) must be based
      // above that to remain visible/usable inside modals. Message (base+1010)
      // and notification (base+1050) derive from this token and stay on top.
      zIndexPopupBase: 10050,
    },
    components: {
      Button: {
        colorBgTextHover: themeColors.primary.light,
        colorLink: themeColors.primary.dark,
        colorText: themeColors.primary.dark,
        defaultBorderColor: themeColors.neutral.black,
      },
      Cascader: {
        dropdownHeight: "auto" as any, //According to the ant docs, this type should actually be `string | number`. This may be a type issue in 5.12.0
      },
      Statistic: {
        colorTextDescription: themeColors.neutral.black,
      },
      Slider: {
        handleActiveColor: themeColors.primary.mid,
        handleColor: themeColors.primary.main,
        trackBg: themeColors.primary.main,
        trackHoverBg: themeColors.primary.mid,
      },
      Segmented: {
        itemSelectedBg: themeColors.primary.main,
        itemSelectedColor: themeColors.neutral.white,
      },
      Tag: {
        colorSuccess: themeColors.success.dark,
        colorSuccessBg: themeColors.success.light,
        colorSuccessBorder: themeColors.success.mid,

        colorWarning: themeColors.warning.dark,
        colorWarningBg: themeColors.warning.light,
        colorWarningBorder: themeColors.warning.mid,

        colorError: themeColors.error.dark,
        colorErrorBg: themeColors.error.light,
        colorErrorBorder: themeColors.error.mid,

        colorInfo: themeColors.info.dark,
        colorInfoBg: themeColors.info.light,
        colorInfoBorder: themeColors.info.mid,
      },
      Descriptions: {
        titleColor: themeColors.neutral.black,
      },
      Alert: {
        colorTextHeading: themeColors.neutral.black,
        colorText: themeColors.neutral.black,
      },
      Pagination: {
        colorPrimary: themeColors.primary.main,
        colorPrimaryHover: themeColors.primary.dark,
        colorPrimaryActive: themeColors.primary.mid,
      },
      Collapse: {
        colorTextHeading: themeColors.neutral.black,
        headerBg: themeColors.neutral.white,
      },
      Carousel: {
        colorBgContainer: themeColors.primary.dark,
      },
    },
  };

  //We have to manually attach the themeClass to the document.body here so theming works with portal elements like Modal.
  React.useEffect(() => {
    document.body.classList.add(themeClass);
    return () => {
      document.body.classList.remove(themeClass);
    };
  }, [themeClass]);

  return <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>;
};

export default ThemeContext;
